import React from 'react';
import { ListItemText, Grid, Card, CardHeader, CardContent, CardActions, List, ListItem, Button, Box, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Menu, MenuItem, IconButton } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { useRevolusendApi } from '../../context';
import utils from '../../services/utils.service';
import moment from 'moment';
import { LoadingButton } from '../../components/loadingButton';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { Attachments } from '../../components/attachments';
import DetailsIcon from '@material-ui/icons/Info';
import { CryptoOrder } from '../../services/types/revolusend/cryptoOrder.type';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    cardActions: {
      justifyContent: 'flex-end'
    },
    link: {
      color: 'grey',
      display: 'flex',
      alignItems: 'center',
    },
    icon: {
      marginRight: theme.spacing(0.9),
      fontSize: '1.1rem',
    },
  })
);

const MarkAsRefundDialog = (props: { orderId?: number, open: boolean, onClose: () => void }) => {
  const { open, onClose, orderId } = props;
  const api = useRevolusendApi();
  const { t } = useTranslation('cryptoOrders');
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    setLoading(false);
  }, [open]);

  const markAsRefunded = () => {
    if (!orderId) {
      return;
    }
    utils.runAsync(async () => {
      setLoading(true);
      try {
        await api.changeCryptoOrderStatus(orderId, 'REFUNDED');
        onClose();
      } catch (e) {
      }
    }, (e) => {
      setLoading(false);
    })
  }

  return (
    <Dialog fullWidth open={open} onClose={onClose}>
      <DialogTitle>{t('MARK_AS_REFUNDED_MODAL_TITLE')}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t('MARK_AS_REFUNDED_MODAL_DESCRIPTION')}
        </DialogContentText>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          {t('CANCEL')}
        </Button>
        <LoadingButton color='primary' loading={loading} disabled={loading} onClick={markAsRefunded}>
          {t('MARK_AS_REFUNDED_BUTTON_TITLE')}
        </LoadingButton>
      </DialogActions>
    </Dialog >
  )
}

export const CryptoOrderDetails = (props: RouteComponentProps<{ id: string }>) => {
  const orderId = props.match.params.id;
  const { t } = useTranslation(['cryptoOrders']);
  const classes = useStyles();
  const api = useRevolusendApi();
  const history = useHistory();

  const [orderDetails, setOrderDetails] = React.useState<CryptoOrder | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [isFileSaved, setIsFileSaved] = React.useState(false);
  const [computedTokenPrice, setComputedTokenPrice] = React.useState<number | undefined>(undefined);

  const [showMarkAsRefundModal, setShowMarkAsRefundModal] = React.useState(false);

  const [actionsAnchorEl, setActionsAnchorEl] = React.useState<null | HTMLElement>(null);
  const handleActionsClose = () => {
    setActionsAnchorEl(null);
  };
  const handleActionsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setActionsAnchorEl(event.currentTarget);
  };

  const loadTransactionDetails = () => {
    utils.runAsync(async () => {
      setLoading(true);
      const details = await api.getCryptoOrderDetails(orderId);
      setOrderDetails(details);
      // compute token price once after loading details
      // Guardamos el valor calculado teniendo en cuenta que `fee` puede venir
      // como porcentaje (e.g. 1.5 => 1.5%) o como fracción (e.g. 0.015).
      if (details != null && details.token_price != null && details.fee != null) {
        const price = Number(String(details.token_price).replace(',', '.'));
        let feeNum = Number(String(details.fee).replace(',', '.'));
        if (isNaN(price) || isNaN(feeNum)) {
          setComputedTokenPrice(undefined);
        } else {
          const feeFraction = feeNum / 100;
          setComputedTokenPrice(price * (1 - feeFraction));
        }

      } else {
        setComputedTokenPrice(undefined);
      }
      setLoading(false);
    }, (e) => {
      setLoading(false);
      if (e) {
        console.log(e);
      }
    });
  };

  React.useEffect(() => {
    loadTransactionDetails();
  }, []);

  const handleFileSaved = () => {
    if (orderDetails?.status !== 'EXCEPTION_REFUNDED') {
      setIsFileSaved(true);
    }
  };

  React.useEffect(() => {
    if (isFileSaved) {
      setIsFileSaved(false);
    }
  }, [isFileSaved]);

  if (loading && orderDetails === null) {
    return (
      <Grid container justify='center'>
        <Box marginTop={4}>
          <CircularProgress />
        </Box>
      </Grid>
    );
  }

  const getOrderStatusLabel = () => {
    if (orderDetails === null) return '';
    return orderDetails.status;
  };

  return (
    <>

      <MarkAsRefundDialog
        orderId={orderDetails?.id}
        open={showMarkAsRefundModal}
        onClose={() => {
          setShowMarkAsRefundModal(false);
          loadTransactionDetails();
        }}
      />

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Card>
                <CardHeader title={t('ORDER')} />
                <CardContent>
                  <Grid container>
                    <Grid item xs={12} md={6}>
                      <List dense disablePadding>
                        <ListItem disableGutters>
                          <ListItemText primary={t('ID')} secondary={orderDetails?.id} />
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemText
                            primary={t('TRANSACTION_ID')}
                            secondary={
                              orderDetails?.transaction_id ? (
                                <span style={{ display: 'flex', alignItems: 'center' }}>
                                  <IconButton
                                    size="small"
                                    component="a"
                                    href={`/revolusend/transfers/${orderDetails.transaction_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ marginRight: 4, padding: 0 }}
                                  >
                                    <DetailsIcon />
                                  </IconButton>
                                  {orderDetails.transaction_id}
                                </span>
                              ) : '-'
                            }
                          />
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemText primary={t('EXTERNAL_REFERENCE')} secondary={orderDetails?.external_reference} />
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemText primary={t('CREATED')} secondary={orderDetails?.created ? moment(orderDetails.created).format('DD-MM-YYYY HH:mm:ss') : '-'} />
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemText primary={t('MODIFIED')} secondary={orderDetails?.updated ? moment(orderDetails.updated).format('DD-MM-YYYY HH:mm:ss') : '-'} />
                        </ListItem>
                      </List>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <List dense disablePadding>
                        <ListItem disableGutters>
                          <ListItemText primary={t('AMOUNT')} secondary={`${orderDetails?.amount} ${orderDetails?.coin_symbol}`} />
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemText primary={t('TOKEN_PRICE')} secondary={computedTokenPrice !== undefined ? `${computedTokenPrice} eur` : '-'} />
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemText primary={t('PROVIDER_TOKEN_PRICE')} secondary={`${orderDetails?.token_price} eur`} />
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemText primary={t('FEE')} secondary={`${utils.currencyFormat(orderDetails?.fee)}%`} />
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemText primary={t('RAMPOFF_FIAT_EUR')} secondary={`${utils.currencyFormat(orderDetails?.rampoff_fiat_eur)} eur`} />
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemText primary={t('BLOCKCHAIN')} secondary={orderDetails?.blockchain} />
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemText primary={t('STATUS')} secondary={t(getOrderStatusLabel())} />
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemText primary={t('PAYMENT_RECEIVED')} secondary={orderDetails?.paid ? t('YES') : t('NO')} />
                        </ListItem>
                      </List>
                    </Grid>
                  </Grid>
                </CardContent>
                <CardActions className={classes.cardActions}>
                  <Button variant='contained' color='primary' aria-controls='actions-menu' aria-haspopup='true' onClick={handleActionsClick}>
                    {t('ACTIONS')}
                  </Button>
                  <Menu id='actions-menu' anchorEl={actionsAnchorEl} keepMounted open={Boolean(actionsAnchorEl)} onClose={handleActionsClose}>
                    <MenuItem
                      disabled={orderDetails?.status !== 'REFUND_REQUESTED'}
                      onClick={() => {
                        setShowMarkAsRefundModal(true);
                        handleActionsClose();
                      }}
                    >
                      {t('MARK_AS_REFUNDED')}
                    </MenuItem>
                  </Menu>
                </CardActions>
              </Card>
            </Grid>
            {orderDetails && (
              <Grid item xs={12}>
                <Attachments
                  entity="cryptoOrder"
                  id={orderDetails.id.toString()}
                  onFileSaved={handleFileSaved}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader title={t('PAYMENT')} />
            <CardContent>
              <List dense disablePadding>
                <ListItem disableGutters>
                  <ListItemText primary={t('PAYMENT_WALLET')} secondary={orderDetails?.wallet_address} />
                </ListItem>
              </List>
            </CardContent>
            <CardActions className={classes.cardActions}>
            </CardActions>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};